import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import debounce from "lodash.debounce";
import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import RouteIcon from "@mui/icons-material/Route";
import PlaceIcon from "@mui/icons-material/Place";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import SpeedIcon from "@mui/icons-material/Speed";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import { useTranslation } from "react-i18next";
import { fetchAuthSession } from "../../services/cognitoAuth";
import { useRoutePlan } from "../../features/routePlan/RoutePlanContext";
import { useVesselSettings } from "../../features/vesselSettings/VesselSettingsContext";
import { useMap } from "../../features/map/MapContext";
import {
  createUserRoute,
  updateUserRoute,
  type SmRoutePayload,
} from "../../api/smApi";
import { useAuth } from "../../features/auth/AuthContext";
import {
  buildCloudSaveWaypoints,
  countPassageCoordinates,
} from "../../features/routePlan/utils/buildRoutePayloadWaypoints";
import { formatFixed } from "../../i18n/numberFormat";
import { formatDistance, formatSpeedFromKnots } from "../../i18n/unitFormat";
import { useAppSnackbar } from "../Snackbar/AppSnackbarProvider";
import { trackEvent } from "../../utils/analytics";
import { useChartSettings } from "../../features/chartSettings/ChartSettingsContext";

function fmtLngLat(p: [number, number] | null, locale: string): string {
  if (!p) return "-";
  const [lng, lat] = p;

  // Format latitude: DD°MM.MMM'N/S
  const latDeg = Math.floor(Math.abs(lat));
  const latMin = (Math.abs(lat) - latDeg) * 60;
  const latDir = lat >= 0 ? "N" : "S";
  const latStr = `${latDeg}°${formatFixed(latMin, locale, 1)}'${latDir}`;

  // Format longitude: DD°MM.MMM'E/W
  const lngDeg = Math.floor(Math.abs(lng));
  const lngMin = (Math.abs(lng) - lngDeg) * 60;
  const lngDir = lng >= 0 ? "E" : "W";
  const lngStr = `${lngDeg}°${formatFixed(lngMin, locale, 1)}'${lngDir}`;

  return `${latStr} ${lngStr}`;
}

const CLOUD_AUTOSAVE_STORAGE_KEY = "routePlanCloudAutoSave";

function fmtLegDistanceMeters(
  m: number | null | undefined,
  locale: string,
  useSiUnits: boolean,
): string {
  if (!m || m <= 0) return "—";
  return formatDistance(m, locale, useSiUnits);
}

export function RoutePlanView() {
  const { t, i18n } = useTranslation();
  const routePlan = useRoutePlan();
  const { vesselDraft, vesselHeight, vesselSpeed, openDialog } =
    useVesselSettings();
  const { useSiUnits } = useChartSettings();
  const { flyTo } = useMap();
  const { enqueueSnackbar } = useAppSnackbar();
  const { authStatus, authPhase, profilePhase } = useAuth();
  const locale = i18n.language;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [routeNameDraft, setRouteNameDraft] = useState(
    routePlan.routeName ?? "",
  );
  const [routeNameEdited, setRouteNameEdited] = useState(false);
  const [isEditingRouteName, setIsEditingRouteName] = useState(false);
  const routeNameInputRef = useRef<HTMLInputElement | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [manualSaving, setManualSaving] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [waypointToDelete, setWaypointToDelete] = useState<{
    id: string;
    name: string;
    index: number;
  } | null>(null);

  const [cloudAutoSave, setCloudAutoSave] = useState(() => {
    try {
      const v = localStorage.getItem(CLOUD_AUTOSAVE_STORAGE_KEY);
      return v === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        CLOUD_AUTOSAVE_STORAGE_KEY,
        cloudAutoSave ? "true" : "false",
      );
    } catch {
      // Ignore storage failures
    }
  }, [cloudAutoSave]);

  const routingModeText =
    routePlan.routingMode === "off"
      ? t("toolbar.routingModeOff")
      : routePlan.routingMode === "auto"
        ? t("toolbar.routingModeAuto")
        : t("toolbar.routingModeManual");

  const allWaypoints = routePlan.waypoints;
  const displayWaypoints = (() => {
    if (allWaypoints.length === 0) return [];
    const filtered = allWaypoints.filter((wp) => wp.userpoint !== false);
    const byId = new Set(filtered.map((wp) => wp.id));
    const first = allWaypoints[0];
    const last = allWaypoints[allWaypoints.length - 1];
    const result = [...filtered];

    if (first && !byId.has(first.id)) {
      result.unshift(first);
      byId.add(first.id);
    }
    if (last && !byId.has(last.id)) {
      result.push(last);
    }
    return result;
  })();
  const hasWaypoints = displayWaypoints.length > 0;

  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  useEffect(() => {
    const focusedId = routePlan.focusedWaypointId;
    if (!focusedId) return;

    const el = itemRefs.current[focusedId];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    setHighlightedId(focusedId);

    const timeout = window.setTimeout(() => {
      setHighlightedId((current) => (current === focusedId ? null : current));
      routePlan.setFocusedWaypointId(null);
    }, 1200);

    return () => window.clearTimeout(timeout);
  }, [routePlan.focusedWaypointId, displayWaypoints, routePlan]);

  useEffect(() => {
    if (!routeNameEdited) {
      setRouteNameDraft(routePlan.routeName ?? "");
    }
  }, [routePlan.routeName, routeNameEdited]);

  const profileId = useMemo(
    () => (authStatus.profile as { id?: number } | null)?.id ?? null,
    [authStatus],
  );
  const isAuthenticated = authStatus.isAuthenticated;
  const isCheckingAccount =
    (authPhase === "restoring" && !authStatus.profile) ||
    (isAuthenticated && !profileId && profilePhase === "loading");
  const savedRouteId = routePlan.savedRouteId;

  const normalizeRouteName = useCallback((name: string) => name.trim(), []);
  const draftName = normalizeRouteName(routeNameDraft);
  const savedName = normalizeRouteName(routePlan.routeName);
  const fallbackName = t("routePlan.untitledRoute");
  const effectiveDraftName = draftName || fallbackName;
  const effectiveSavedName = savedName || fallbackName;

  const commitRouteNameEdit = useCallback(() => {
    if (!savedRouteId) {
      setIsEditingRouteName(false);
      return;
    }
    const next = normalizeRouteName(routeNameDraft) || fallbackName;
    setRouteNameDraft(next);
    setRouteNameEdited(false);
    setIsEditingRouteName(false);
    if (next !== effectiveSavedName) {
      routePlan.setRouteName(next);
    }
  }, [
    savedRouteId,
    normalizeRouteName,
    routeNameDraft,
    fallbackName,
    effectiveSavedName,
    routePlan,
  ]);

  const cancelRouteNameEdit = useCallback(() => {
    setRouteNameDraft(routePlan.routeName ?? "");
    setRouteNameEdited(false);
    setIsEditingRouteName(false);
  }, [routePlan.routeName]);

  const handleClearClick = useCallback(() => {
    if (!savedRouteId || routePlan.hasUnsavedChanges) {
      setClearDialogOpen(true);
      return;
    }
    trackEvent("feature_used", { feature: "route_clear" });
    routePlan.clearRoute();
  }, [routePlan, savedRouteId]);

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    [i18n.language],
  );

  const fetchAccessToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    if (!accessToken) {
      throw new Error(t("routePlan.cloudAuthError"));
    }
    return accessToken;
  }, [t]);

  const buildPayload = useCallback(
    (name: string): SmRoutePayload => ({
      name,
      length: routePlan.totalDistance,
      waypoints: buildCloudSaveWaypoints(
        routePlan.waypoints,
        routePlan.loadedRouteAllPoints,
      ),
    }),
    [
      routePlan.totalDistance,
      routePlan.waypoints,
      routePlan.loadedRouteAllPoints,
    ],
  );

  const syncInFlightRef = useRef(false);
  const debouncedSyncRef = useRef<ReturnType<typeof debounce> | null>(null);
  const autoSyncRef = useRef<() => Promise<void>>(() => Promise.resolve());

  autoSyncRef.current = async () => {
    if (!savedRouteId || !profileId) return;
    if (!isAuthenticated) return;
    if (syncInFlightRef.current || manualSaving) return;

    syncInFlightRef.current = true;
    setSyncError(null);
    try {
      const token = await fetchAccessToken();
      const payload = buildPayload(effectiveSavedName);
      if (import.meta.env.DEV) {
        console.groupCollapsed(
          "[RoutePlanCloudSave] autoSync payload",
        );
        console.log({
          savedRouteId,
          name: payload.name,
          waypointsCount: payload.waypoints.length,
          loadedRouteAllPointsCount:
            routePlan.loadedRouteAllPoints?.length ?? null,
          passageCoordinateCount: countPassageCoordinates(routePlan.waypoints),
          trackedWaypointsInReduced:
            routePlan.waypoints.filter((wp) => wp.tracked === true).length,
          usingLoadedRouteAllPoints: Boolean(
            routePlan.loadedRouteAllPoints &&
              routePlan.loadedRouteAllPoints.length > 0,
          ),
          payload,
        });
        console.groupEnd();
      }
      if (
        routePlan.waypoints.some((wp) => wp.tracked === true) &&
        payload.waypoints.length <= routePlan.waypoints.length &&
        countPassageCoordinates(routePlan.waypoints) >
          routePlan.waypoints.length + 1
      ) {
        throw new Error(t("routePlan.cloudSaveError"));
      }
      await updateUserRoute(token, profileId, savedRouteId, payload);
      routePlan.markRouteSaved({ routeName: payload.name });
      setLastSyncedAt(Date.now());
    } catch (err) {
      const known = new Set<string>([t("routePlan.cloudAuthError")]);
      const msg =
        err instanceof Error && known.has(err.message)
          ? err.message
          : t("routePlan.cloudSaveError");
      setSyncError(msg);
    } finally {
      syncInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!savedRouteId || !isAuthenticated) {
      debouncedSyncRef.current?.cancel();
      debouncedSyncRef.current = null;
      return;
    }
    const debounced = debounce(() => {
      autoSyncRef.current();
    }, 1200);
    debouncedSyncRef.current = debounced;
    return () => {
      debounced.cancel();
    };
  }, [savedRouteId, isAuthenticated]);

  useEffect(() => {
    if (!savedRouteId || !isAuthenticated) return;
    if (!cloudAutoSave) return;
    if (!routePlan.hasUnsavedChanges) return;
    if (!routePlan.hasUserEdits) return;
    if (routePlan.status === "loading") return;
    if (manualSaving) return;
    debouncedSyncRef.current?.();
  }, [
    savedRouteId,
    isAuthenticated,
    cloudAutoSave,
    routePlan.hasUnsavedChanges,
    routePlan.hasUserEdits,
    routePlan.status,
    routePlan.waypoints,
    routePlan.routingMode,
    routePlan.totalDistance,
    manualSaving,
  ]);

  const handleCloudSave = useCallback(async () => {
    if (!isAuthenticated) {
      setSyncError(t("routePlan.cloudLoginRequired"));
      return;
    }
    if (!profileId) {
      setSyncError(t("routePlan.cloudProfileMissing"));
      return;
    }
    if (routePlan.waypoints.length === 0) {
      setSyncError(t("routePlan.cloudNoWaypoints"));
      return;
    }

    trackEvent("feature_used", { feature: "route_cloud_save" });
    setManualSaving(true);
    setSyncError(null);
    try {
      const token = await fetchAccessToken();
      const payload = buildPayload(effectiveDraftName);
      if (import.meta.env.DEV) {
        console.groupCollapsed("[RoutePlanCloudSave] manual save payload");
        console.log({
          savedRouteId,
          name: payload.name,
          waypointsCount: payload.waypoints.length,
          loadedRouteAllPointsCount:
            routePlan.loadedRouteAllPoints?.length ?? null,
          passageCoordinateCount: countPassageCoordinates(routePlan.waypoints),
          trackedWaypointsInReduced:
            routePlan.waypoints.filter((wp) => wp.tracked === true).length,
          usingLoadedRouteAllPoints: Boolean(
            routePlan.loadedRouteAllPoints &&
              routePlan.loadedRouteAllPoints.length > 0,
          ),
          payload,
        });
        console.groupEnd();
      }
      if (
        routePlan.waypoints.some((wp) => wp.tracked === true) &&
        payload.waypoints.length <= routePlan.waypoints.length &&
        countPassageCoordinates(routePlan.waypoints) >
          routePlan.waypoints.length + 1
      ) {
        // Refuse to overwrite a dense tracked route with sparse planner points.
        throw new Error(t("routePlan.cloudSaveError"));
      }
      if (savedRouteId) {
        await updateUserRoute(token, profileId, savedRouteId, payload);
      } else {
        const created = await createUserRoute(token, profileId, payload);
        routePlan.setSavedRouteId(created.id ?? null);
        routePlan.setLoadedRouteEditable(true);
      }
      routePlan.setRouteName(payload.name);
      routePlan.markRouteSaved({ routeName: payload.name });
      setRouteNameDraft(payload.name);
      setRouteNameEdited(false);
      setLastSyncedAt(Date.now());
    } catch (err) {
      const known = new Set<string>([t("routePlan.cloudAuthError")]);
      const msg =
        err instanceof Error && known.has(err.message)
          ? err.message
          : t("routePlan.cloudSaveError");
      setSyncError(msg);
    } finally {
      setManualSaving(false);
    }
  }, [
    isAuthenticated,
    profileId,
    routePlan,
    savedRouteId,
    fetchAccessToken,
    buildPayload,
    effectiveDraftName,
    t,
  ]);

  const handleOpenLogin = useCallback(() => {
    window.dispatchEvent(new CustomEvent("open-auth-dialog"));
  }, []);

  const cloudSubtitle = useMemo(() => {
    if (!savedRouteId) {
      return t("routePlan.cloudSubtitle");
    }
    if (!cloudAutoSave) {
      return t("routePlan.cloudSaveManually");
    }
    if (lastSyncedAt) {
      return t("routePlan.cloudLastSynced", {
        time: timeFormatter.format(new Date(lastSyncedAt)),
      });
    }
    return t("routePlan.cloudAutoSync");
  }, [savedRouteId, cloudAutoSave, lastSyncedAt, timeFormatter, t]);

  const showCloudSaveButton =
    !savedRouteId || (!cloudAutoSave && routePlan.hasUnsavedChanges);
  const cloudSaveButtonLabel =
    savedRouteId && !cloudAutoSave
      ? t("routePlan.cloudSaveChanges")
      : t("routePlan.cloudSave");
  const cloudSaveDisabled =
    manualSaving ||
    routePlan.status === "loading" ||
    routePlan.waypoints.length === 0;

  const lastShownSyncErrorRef = useRef<string | null>(null);
  const lastShownRouteErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (!syncError) return;
    if (syncError === lastShownSyncErrorRef.current) return;
    lastShownSyncErrorRef.current = syncError;
    enqueueSnackbar({ message: syncError, severity: "error" });
  }, [enqueueSnackbar, syncError]);

  useEffect(() => {
    const routeError =
      routePlan.status === "error" ? (routePlan.error ?? null) : null;
    if (!routeError) return;
    if (routeError === lastShownRouteErrorRef.current) return;
    lastShownRouteErrorRef.current = routeError;
    enqueueSnackbar({ message: routeError, severity: "error" });
  }, [enqueueSnackbar, routePlan.error, routePlan.status]);

  return (
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      <Stack spacing={1.5}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: "rgba(17,24,39,0.10)",
              color: "rgba(17,24,39,0.85)",
              width: 32,
              height: 32,
            }}
          >
            <RouteIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.1 }}
            >
              {t("routePlan.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {routingModeText}
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 1,
            borderRadius: 1.5,
            border: "1px solid rgba(17,24,39,0.08)",
            background: "rgba(255,255,255,0.72)",
          }}
        >
          <Stack spacing={0.75}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(17,24,39,0.10)",
                  color: "rgba(17,24,39,0.85)",
                  width: 24,
                  height: 24,
                }}
              >
                <DirectionsBoatIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, lineHeight: 1.1, flex: 1 }}
              >
                {t("vesselSettings.title")}
              </Typography>
              <Tooltip title={t("vesselSettings.title")} placement="top" arrow>
                <IconButton
                  size="small"
                  onClick={openDialog}
                  aria-label={t("vesselSettings.title")}
                  sx={{
                    border: "1px solid rgba(17,24,39,0.12)",
                    bgcolor: "rgba(17,24,39,0.04)",
                    "&:hover": {
                      bgcolor: "rgba(17,24,39,0.10)",
                    },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.75,
                  py: 0.35,
                  borderRadius: 1,
                  border: "1px solid rgba(17,24,39,0.12)",
                  bgcolor: "rgba(17,24,39,0.04)",
                  minHeight: 24,
                }}
              >
                <VerticalAlignBottomIcon
                  sx={{ fontSize: 16, color: "rgba(17,24,39,0.7)" }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: 0.2, lineHeight: 1 }}
                >
                  {formatFixed(vesselDraft, locale, 1)} m
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.75,
                  py: 0.35,
                  borderRadius: 1,
                  border: "1px solid rgba(17,24,39,0.12)",
                  bgcolor: "rgba(17,24,39,0.04)",
                  minHeight: 24,
                }}
              >
                <VerticalAlignTopIcon
                  sx={{ fontSize: 16, color: "rgba(17,24,39,0.7)" }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: 0.2, lineHeight: 1 }}
                >
                  {formatFixed(vesselHeight, locale, 1)} m
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.75,
                  py: 0.35,
                  borderRadius: 1,
                  border: "1px solid rgba(17,24,39,0.12)",
                  bgcolor: "rgba(17,24,39,0.04)",
                  minHeight: 24,
                }}
              >
                <SpeedIcon sx={{ fontSize: 16, color: "rgba(17,24,39,0.7)" }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: 0.2, lineHeight: 1 }}
                >
                  {formatSpeedFromKnots(vesselSpeed, locale, useSiUnits)}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            borderRadius: 2,
            border: "1px solid rgba(25,118,210,0.12)",
            background:
              "linear-gradient(180deg, rgba(245,249,255,0.96) 0%, rgba(255,255,255,0.86) 100%)",
            boxShadow: "0 10px 24px rgba(15,23,42,0.04)",
          }}
        >
          <Stack spacing={1.1}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(25,118,210,0.10)",
                  color: "#1976d2",
                  width: 30,
                  height: 30,
                  mt: 0.15,
                }}
              >
                <CloudSyncIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 800, lineHeight: 1.15 }}
                >
                  {t("routePlan.cloudTitle")}
                </Typography>
              </Box>
            </Box>

            {isAuthenticated && !isCheckingAccount && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.25,
                  px: 1,
                  py: 0.9,
                  borderRadius: 1.5,
                  border: "1px solid rgba(17,24,39,0.08)",
                  bgcolor: "rgba(255,255,255,0.72)",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, lineHeight: 1.15 }}
                  >
                    {t("routePlan.cloudAutoSaveLabel")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 0.2,
                      lineHeight: 1.3,
                    }}
                  >
                    {cloudAutoSave
                      ? t("routePlan.cloudAutoSync")
                      : t("routePlan.cloudSaveManually")}
                  </Typography>
                </Box>
                <Switch
                  size="small"
                  checked={cloudAutoSave}
                  onChange={(event) => {
                    setCloudAutoSave(event.target.checked);
                    trackEvent("feature_used", {
                      feature: "route_cloud_autosave_toggle",
                    });
                  }}
                  inputProps={{
                    "aria-label": t("routePlan.cloudAutoSaveLabel"),
                  }}
                />
              </Box>
            )}

            {isCheckingAccount ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.9,
                  borderRadius: 1.25,
                  border: "1px solid rgba(17,24,39,0.08)",
                  bgcolor: "rgba(17,24,39,0.03)",
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  {t("routePlan.cloudCheckingAccount")}
                </Typography>
              </Box>
            ) : !isAuthenticated ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  px: 1,
                  py: 0.9,
                  borderRadius: 1.5,
                  border: "1px solid rgba(25,118,210,0.14)",
                  bgcolor: "rgba(25,118,210,0.05)",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#1976d2",
                    bgcolor: "rgba(25,118,210,0.10)",
                    flex: "0 0 auto",
                  }}
                >
                  <LoginIcon sx={{ fontSize: 16 }} />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.35,
                    flex: 1,
                    minWidth: 0,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                  }}
                >
                  {t("routePlan.cloudLoginTitle")}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleOpenLogin}
                  sx={{ whiteSpace: "nowrap", minWidth: "auto", px: 1 }}
                >
                  {t("routePlan.cloudLoginCta")}
                </Button>
              </Box>
            ) : (
              <>
                <Stack spacing={1}>
                  {savedRouteId ? (
                    isEditingRouteName ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.75,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={t("routePlan.cloudNameLabel")}
                          value={routeNameDraft}
                          inputRef={routeNameInputRef}
                          onChange={(event) => {
                            setRouteNameDraft(event.target.value);
                            setRouteNameEdited(true);
                          }}
                          onBlur={commitRouteNameEdit}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              commitRouteNameEdit();
                            }
                            if (event.key === "Escape") {
                              event.preventDefault();
                              cancelRouteNameEdit();
                            }
                          }}
                          inputProps={{ maxLength: 60 }}
                          sx={{ minWidth: 0, flex: 1 }}
                        />
                        <Tooltip title={t("common.done")} placement="top" arrow>
                          <IconButton
                            size="small"
                            aria-label={t("common.done")}
                            onClick={() => commitRouteNameEdit()}
                            sx={{
                              width: 34,
                              height: 34,
                              mt: 0.25,
                              borderRadius: "50%",
                              border: "1px solid rgba(17,24,39,0.12)",
                              bgcolor: "rgba(46,125,50,0.12)",
                              color: "rgba(46,125,50,0.9)",
                              "&:hover": {
                                bgcolor: "rgba(46,125,50,0.2)",
                              },
                            }}
                          >
                            <CheckIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.cancel")} placement="top" arrow>
                          <IconButton
                            size="small"
                            aria-label={t("common.cancel")}
                            onClick={() => cancelRouteNameEdit()}
                            sx={{
                              width: 34,
                              height: 34,
                              mt: 0.25,
                              borderRadius: "50%",
                              border: "1px solid rgba(17,24,39,0.12)",
                              bgcolor: "rgba(17,24,39,0.05)",
                              "&:hover": { bgcolor: "rgba(17,24,39,0.08)" },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1,
                          py: 0.95,
                          borderRadius: 1.5,
                          border: "1px solid rgba(17,24,39,0.08)",
                          bgcolor: "rgba(255,255,255,0.72)",
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mb: 0.2,
                              lineHeight: 1.1,
                            }}
                          >
                            {t("routePlan.cloudNameLabel")}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={effectiveSavedName}
                          >
                            {effectiveSavedName}
                          </Typography>
                        </Box>
                        <Tooltip title={t("common.edit")} placement="top" arrow>
                          <IconButton
                            size="small"
                            aria-label={t("common.edit")}
                            onClick={() => {
                              setIsEditingRouteName(true);
                              setRouteNameEdited(true);
                              window.setTimeout(() => {
                                routeNameInputRef.current?.focus();
                                routeNameInputRef.current?.select?.();
                              }, 0);
                            }}
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              border: "1px solid rgba(17,24,39,0.12)",
                              bgcolor: "rgba(17,24,39,0.05)",
                              "&:hover": { bgcolor: "rgba(17,24,39,0.08)" },
                            }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label={t("routePlan.cloudNameLabel")}
                      placeholder={t("routePlan.namePlaceholder")}
                      value={routeNameDraft}
                      onChange={(event) => {
                        setRouteNameDraft(event.target.value);
                        setRouteNameEdited(true);
                      }}
                      inputProps={{ maxLength: 60 }}
                      helperText={undefined}
                    />
                  )}
                  {!!savedRouteId && cloudAutoSave && !routePlan.hasUnsavedChanges && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: "1px dashed rgba(46,125,50,0.24)",
                        bgcolor: "rgba(46,125,50,0.04)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(46,125,50,0.9)",
                          fontWeight: 700,
                          lineHeight: 1.3,
                        }}
                      >
                        {cloudSubtitle}
                      </Typography>
                    </Box>
                  )}
                  {showCloudSaveButton && (
                    <Button
                      variant="contained"
                      size="medium"
                      fullWidth
                      onClick={handleCloudSave}
                      endIcon={
                        manualSaving ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : null
                      }
                      disabled={
                        cloudSaveDisabled
                      }
                      sx={{
                        whiteSpace: "nowrap",
                        minHeight: 40,
                        borderRadius: 1.6,
                        fontSize: 13,
                        fontWeight: 700,
                        boxShadow: "none",
                      }}
                    >
                      {cloudSaveButtonLabel}
                    </Button>
                  )}
                </Stack>
              </>
            )}

            {/* Errors are surfaced via snackbars */}
          </Stack>
        </Paper>

        {/* Status */}
        {routePlan.status === "loading" && (
          <Paper
            elevation={0}
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 2,
              border: "1px solid rgba(17,24,39,0.08)",
              background: "rgba(255,255,255,0.72)",
            }}
          >
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              {t("routePlan.calculating")}
            </Typography>
            <LinearProgress />
          </Paper>
        )}
        {/* Route calculation errors are surfaced via snackbars */}

        {/* Waypoints */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid rgba(17,24,39,0.08)",
            background: "rgba(255,255,255,0.72)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 1.25,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "rgba(17,24,39,0.08)",
                  color: "rgba(17,24,39,0.8)",
                }}
              >
                <PlaceIcon fontSize="small" />
              </Avatar>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t("routePlan.waypoints")} ({displayWaypoints.length})
              </Typography>
            </Box>
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={handleClearClick}
              disabled={
                !hasWaypoints ||
                routePlan.status === "loading"
              }
            >
              {t("routePlan.clear")}
            </Button>
          </Box>

          <Divider />

          {!hasWaypoints ? (
            <Box sx={{ p: 1.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("routePlan.emptyTitle")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                {routePlan.routingMode === "off"
                  ? t("routePlan.emptyHintOff")
                  : t("routePlan.emptyHintOn")}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {displayWaypoints.map((wp, idx) => {
                const isStart = idx === 0;
                const isLast = idx === displayWaypoints.length - 1;
                const label =
                  wp.name || `${t("routePlan.waypoint")} ${idx + 1}`;
                const role = isStart
                  ? t("routePlan.start")
                  : isLast
                    ? t("routePlan.finish")
                    : undefined;
                const isHighlighted = highlightedId === wp.id;
                return (
                  <ListItem
                    component="div"
                    ref={(el: HTMLDivElement | null) => {
                      itemRefs.current[wp.id] = el;
                    }}
                    key={wp.id}
                    disablePadding
                    secondaryAction={
                      !wp.tracked && routePlan.loadedRouteEditable ? (
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWaypointToDelete({
                              id: wp.id,
                              name:
                                wp.name ||
                                `${t("routePlan.waypoint")} ${idx + 1}`,
                              index: idx,
                            });
                            setDeleteDialogOpen(true);
                          }}
                          size="small"
                          disabled={routePlan.status === "loading"}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : null
                    }
                  >
                    <ListItemButton
                      onClick={() => flyTo(wp.lngLat)}
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        "&:hover": {
                          backgroundColor: "rgba(17,24,39,0.03)",
                        },
                        transition:
                          "background-color 0.4s ease, transform 0.3s ease",
                        ...(isHighlighted && {
                          backgroundColor: "rgba(25,118,210,0.12)",
                          transform: "scale(1.01)",
                        }),
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 26,
                          height: 26,
                          mr: 1.25,
                          fontSize: 12,
                          bgcolor:
                            wp.routingMode === "auto"
                              ? "rgba(25,118,210,0.12)"
                              : "rgba(156,39,176,0.12)",
                          color:
                            wp.routingMode === "auto"
                              ? "rgba(25,118,210,0.95)"
                              : "rgba(156,39,176,0.95)",
                          border: "1px solid rgba(17,24,39,0.08)",
                        }}
                      >
                        {idx + 1}
                      </Avatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                              noWrap
                            >
                              {label}
                            </Typography>
                            {role && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={role}
                                sx={{ height: 20 }}
                              />
                            )}
                            <Chip
                              size="small"
                              label={
                                wp.tracked
                                  ? t("routePlan.trackedWaypointChip")
                                  : wp.routingMode === "auto"
                                    ? t("routePlan.waypointModeAuto")
                                    : t("routePlan.waypointModeManual")
                              }
                              sx={{
                                height: 20,
                                ml: role ? 0.5 : 0,
                                bgcolor: wp.tracked
                                  ? "rgba(25,118,210,0.12)" // blue
                                  : wp.routingMode === "auto"
                                    ? "rgba(46,125,50,0.12)" // green
                                    : "rgba(211,47,47,0.12)", // red
                                color: wp.tracked
                                  ? "#1976d2"
                                  : wp.routingMode === "auto"
                                    ? "rgba(46,125,50,0.9)"
                                    : "rgba(211,47,47,0.9)",
                              }}
                            />
                            <Box sx={{ flex: 1 }} />
                          </Box>
                        }
                        secondary={
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {fmtLngLat(wp.lngLat, locale)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              •
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {isStart
                                ? "—"
                                : fmtLegDistanceMeters(wp.distance, locale, useSiUnits)}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: "div" }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      </Stack>

      {/* Clear route confirmation dialog (cloud changes pending) */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        aria-labelledby="clear-route-dialog-title"
        aria-describedby="clear-route-dialog-description"
      >
        <DialogTitle id="clear-route-dialog-title">
          {t("routePlan.clearConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-route-dialog-description">
            {t("routePlan.clearConfirmBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} color="inherit">
            {t("routePlan.cancel")}
          </Button>
          <Button
            onClick={() => {
              trackEvent("feature_used", { feature: "route_clear" });
              routePlan.clearRoute();
              setClearDialogOpen(false);
            }}
            color="error"
            variant="contained"
            disabled={routePlan.status === "loading"}
          >
            {t("routePlan.clear")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-waypoint-dialog-title"
        aria-describedby="delete-waypoint-dialog-description"
      >
        <DialogTitle id="delete-waypoint-dialog-title">
          {t("routePlan.deleteWaypointTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-waypoint-dialog-description">
            {waypointToDelete && (
              <>
                {t("routePlan.deleteWaypointMessage", {
                  waypoint: waypointToDelete.name,
                  index: waypointToDelete.index + 1,
                })}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            {t("routePlan.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (waypointToDelete) {
                routePlan.removeWaypoint(waypointToDelete.id);
                setDeleteDialogOpen(false);
                setWaypointToDelete(null);
              }
            }}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            disabled={routePlan.status === "loading"}
          >
            {t("routePlan.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
